// Scheduled as a week (docs/56, round 3, alternative A): the Scheduled list's day headers and rows in columns, as many
// as fit the width (the rest by scrolling sideways), and "Unscheduled" under them with the Inbox, Anytime and Someday
// in the same columns. A to-do dropped on a day starts that day; dropped below, it waits there. Nothing stored is new.

import { Fragment, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import type { ListSection, Lists } from "../core/lists";
import type { Todo } from "../core/records";
import type { Day } from "../core/types";
import { bandCount, bandFiltered, bandRoute, bandSectionID, columnWidth, droppedOnBand, droppedOnDay, paged, visibleColumns, WEEK_BANDS, WEEK_DAYS, WEEK_ROW_LIMIT, weekDays, type WeekBand, type WeekDay } from "../core/week";
import { key, t } from "../i18n";
import { useWindow, type UI } from "./context";
import { columnDropProps, columnRowDropProps, draggedIDs, headerDropProps } from "./dnd";
import { dayHeading, routeTitle, splitEmoji } from "./format";
import { Icon } from "./Icon";
import { ViewSwitch } from "./Menu";
import { setWeekPref, usePrefs } from "./prefs";
import { QuickEntryCard } from "./QuickEntryCard";
import { ProjectMark } from "./Sidebar";
import { TodoCard } from "./TodoCard";
import { TodoRow } from "./TodoRow";

/** One column as the keys move through it: the week's days, then the groups of "Unscheduled". */
export interface WeekColumn {
  id: string;
  day?: Day;
  /** The to-dos it shows, top to bottom ("Show N More" leaves the rest out until asked). */
  ids: string[];
}

/**
 * What the week works out: its days, the tab's groups and the three tabs' counts. Once per change of the records or the
 * week's own state, never when a to-do opens (Bastian, 2026-10-20: opening one in the week was slow).
 */
export interface WeekBase {
  start: Day;
  days: WeekDay[];
  tab: WeekBand;
  showsBand: boolean;
  /** The tab's groups, narrowed by the filter. */
  band: ListSection[];
  counts: Record<WeekBand, number>;
}

/** What the week shows: its base, with each column's rows as far as "Show N More". */
export interface WeekView extends WeekBase {
  columns: WeekColumn[];
  /** The to-dos past each column's first eight, when not unfolded. */
  hidden: Record<string, number>;
}

const BAND_TITLES: Record<WeekBand, string> = { inbox: key("Inbox"), anytime: key("Anytime"), someday: key("Someday") };
/** The space between the columns. */
const GAP = 24;
/** "Unscheduled" dragged: at least its bar and about a row; the days keep at least this much (as on the Mac). */
const BAND_MIN = 120;
const DAYS_MIN = 220;

export function weekBase(lists: Lists, weekStart: Day | null, filter: string, tab: WeekBand, showsBand: boolean): WeekBase {
  const start = weekStart !== null && weekStart > lists.today ? weekStart : lists.today;
  const counts = Object.fromEntries(WEEK_BANDS.map((one) => [one, bandCount(lists, one)])) as Record<WeekBand, number>;
  return { start, days: weekDays(lists, start, WEEK_DAYS), tab, showsBand, band: showsBand ? bandFiltered(lists, tab, filter) : [], counts };
}

export function weekView(base: WeekBase, ui: Pick<UI, "weekExpanded" | "open">): WeekView {
  const hidden: Record<string, number> = {};
  const keep = new Set(ui.open !== null ? [ui.open] : []);
  const limited = (id: string, todos: readonly Todo[]) => {
    if (ui.weekExpanded.includes(id) || todos.length <= WEEK_ROW_LIMIT + 1) return todos.map((todo) => todo.id);
    const shown = todos.filter((todo, index) => index < WEEK_ROW_LIMIT || keep.has(todo.id));
    hidden[id] = todos.length - shown.length;
    return shown.map((todo) => todo.id);
  };
  const columns: WeekColumn[] = [
    ...base.days.map((day) => ({ id: `day:${day.day}`, day: day.day, ids: limited(`day:${day.day}`, day.todos) })),
    ...base.band.map((section) => ({ id: `band:${section.id}`, ids: limited(`band:${section.id}`, section.todos) })),
  ];
  return { ...base, columns, hidden };
}

const reducesMotion = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** The column that holds a to-do, for the keys. */
export function columnOf(week: WeekView, id: string | undefined): number {
  return id === undefined ? -1 : week.columns.findIndex((column) => column.ids.includes(id));
}

// MARK: - The heading's controls

/** List | Week beside Scheduled's title; in the week the pager: « a week, ‹ a day, This Week, › a day, » a week. */
export function ScheduledControls() {
  const { state, ui, set } = useWindow();
  const prefs = usePrefs();
  const week = prefs.week.shows;
  const start = ui.weekStart !== null && ui.weekStart > state.today ? ui.weekStart : state.today;
  const page = (days: number) => {
    const next = paged(start, days, state.today);
    set({ weekStart: next === state.today ? null : next });
  };
  return (
    <div className="week-controls">
      {week && (
        <div className="week-pager" role="group" aria-label={t("Page Through the Week")}>
          <button className="pager-button" onClick={() => page(-WEEK_DAYS)} disabled={start === state.today} aria-label={t("A Week Earlier")} title={`${t("A Week Earlier")} (⇧[)`}><Icon name="chevsLeft" /></button>
          <button className="pager-button" onClick={() => page(-1)} disabled={start === state.today} aria-label={t("A Day Earlier")} title={t("A Day Earlier")}><Icon name="chevLeft" /></button>
          <button className="small-button" onClick={() => set({ weekStart: null })} disabled={start === state.today} title={`${t("This Week")} (0)`}>{t("This Week")}</button>
          <button className="pager-button" onClick={() => page(1)} aria-label={t("A Day Later")} title={t("A Day Later")}><Icon name="chevRight" /></button>
          <button className="pager-button" onClick={() => page(WEEK_DAYS)} aria-label={t("A Week Later")} title={`${t("A Week Later")} (⇧])`}><Icon name="chevsRight" /></button>
        </div>
      )}
      <ViewSwitch label={t("Show Scheduled As")} value={week ? "week" : "list"}
        options={[{ value: "list", label: t("List") }, { value: "week", label: t("Week"), title: `${t("Week")} (Y)` }]}
        onChange={(value) => { setWeekPref({ shows: value === "week" }); if (value === "week") set({ selected: [] }); }} />
    </div>
  );
}

// MARK: - The week

export function WeekBoard({ week, onComplete }: { week: WeekView; onComplete: (ids: readonly string[]) => void }) {
  const window = useWindow();
  const { state, model } = window;
  const prefs = usePrefs();
  const scroller = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const observer = new ResizeObserver(() => setWidth(element.clientWidth));
    observer.observe(element);
    setWidth(element.clientWidth);
    return () => observer.disconnect();
  }, []);
  const column = width > 0 ? columnWidth(width, GAP) : 260;

  // Paging, a day or a week alike: the first day back at the left edge, and the days slide in from the side the week
  // goes to (Bastian, 2026-10-20: a week further wasn't animated). None with Reduce Motion.
  const shownStart = useRef(week.start);
  useLayoutEffect(() => {
    const element = scroller.current;
    const before = shownStart.current;
    shownStart.current = week.start;
    if (!element || before === week.start) return;
    element.scrollTo({ left: 0 });
    if (reducesMotion() || typeof element.animate !== "function") return;
    const from = week.start > before ? 48 : -48;
    element.animate([{ transform: `translateX(${from}px)`, opacity: 0.35 }, { transform: "none", opacity: 1 }],
      { duration: 240, easing: "cubic-bezier(.2, .8, .2, 1)" });
  }, [week.start]);

  const moving = (ids: readonly string[]) => state.records.todos.filter((todo) => ids.includes(todo.id))
    .sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
  const dropOnDay = (day: Day, ids: readonly string[], before: string | undefined) => {
    const changed = droppedOnDay(state.lists, state.records.todos, moving(ids), day, before, state.zone);
    if (changed.length) model.updateMany(changed, "Moved");
  };
  const dropOnBand = (tab: WeekBand, ids: readonly string[], before: string | undefined) => {
    const changed = droppedOnBand(state.lists, state.records.todos, moving(ids), tab, before, state.zone);
    if (changed.length) model.updateMany(changed, "Moved");
  };
  const byID = new Map(state.records.todos.map((todo) => [todo.id, todo]));
  const columnByID = new Map(week.columns.map((one) => [one.id, one]));
  const bare = !prefs.week.showsDetails;

  return (
    <div className="week" role="group" aria-label={t("Week")}>
      <div className="week-days" ref={scroller} style={{ ["--week-column" as string]: `${column}px`, ["--week-gap" as string]: `${GAP}px` }}>
        {week.days.map((day) => {
          const id = `day:${day.day}`;
          const shown = (columnByID.get(id)?.ids ?? []).flatMap((todoID) => byID.get(todoID) ?? []);
          return (
            <DayColumn key={day.day} day={day} todos={shown} hidden={week.hidden[id] ?? 0} columnID={id} bare={bare}
              onDrop={(ids, before) => dropOnDay(day.day, ids, before)} onComplete={onComplete} />
          );
        })}
      </div>
      <Unscheduled week={week} width={column} visible={width > 0 ? visibleColumns(width, GAP) : 1} bare={bare}
        bandHeight={prefs.week.bandHeight} onDrop={dropOnBand} onComplete={onComplete} byID={byID} />
    </div>
  );
}

function DayColumn({ day, todos, hidden, columnID, bare, onDrop, onComplete }: {
  day: WeekDay; todos: Todo[]; hidden: number; columnID: string; bare: boolean;
  onDrop: (ids: readonly string[], before: string | undefined) => void; onComplete: (ids: readonly string[]) => void;
}) {
  const window = useWindow();
  const { state, ui, set, go, compose } = window;
  const { lists } = state;
  const heading = dayHeading(day.day, lists.today);
  const route = day.isToday ? { kind: "today" as const } : { kind: "scheduled" as const };
  const composing = ui.composing?.day === day.day ? ui.composing : null;
  const name = day.isToday ? t("Today") : heading.title;
  return (
    <section className={`week-day${day.isToday ? " today" : ""}`} aria-label={name} data-day={day.day} {...columnDropProps((ids) => onDrop(ids, undefined))}>
      <div className="day-head week-head" {...headerDropProps(window, (ids) => onDrop(ids, todos[0]?.id))}>
        <b>{heading.title}</b>{heading.detail && <span>{heading.detail}</span>}
        <span className="spacer" />
        <button className="week-add" aria-label={t("Add To-Do on {day}", { day: name })} title={`${t("Add To-Do")} (Q)`}
          onClick={() => compose({ day: day.day })}>
          <Icon name="plusThin" />
        </button>
      </div>
      {day.birthdays.map((birthday) => (
        <div key={birthday.id} className="birthday">
          <Icon name="gift" /><b>{birthday.name}</b>
          {birthday.year !== undefined && <span>{t("turns {count}", { count: Number(day.day.slice(0, 4)) - birthday.year })}</span>}
        </div>
      ))}
      {day.projects.map((project) => (
        <button key={project.id} className="row project-row" onClick={() => go({ kind: "project", id: project.id })}>
          <ProjectMark project={project} progress={lists.progress(project)} />
          <span className="title">{project.title}</span>
          <span />
        </button>
      ))}
      {todos.map((todo, index) => (
        <Fragment key={todo.id}>
          {ui.open === todo.id ? <TodoCard todo={todo} /> : (
            <TodoRow todo={todo} route={route} showsPlace inToday={day.isToday} onComplete={onComplete} bare={bare}
              drop={columnRowDropProps(todo, todos[index + 1]?.id, onDrop)} />
          )}
        </Fragment>
      ))}
      {composing && <QuickEntryCard route={route} composing={composing} />}
      {hidden > 0 && (
        <button className="link week-more" onClick={() => set({ weekExpanded: [...ui.weekExpanded, columnID] })}>
          {t("Show {count} More", { count: hidden })}
        </button>
      )}
    </section>
  );
}

// MARK: - Unscheduled

function Unscheduled({ week, width, visible, bare, bandHeight, onDrop, onComplete, byID }: {
  week: WeekView; width: number; visible: number; bare: boolean; bandHeight: number; byID: Map<string, Todo>;
  onDrop: (tab: WeekBand, ids: readonly string[], before: string | undefined) => void; onComplete: (ids: readonly string[]) => void;
}) {
  const window = useWindow();
  const { ui, set } = window;
  const filter = useRef<HTMLInputElement>(null);
  const band = useRef<HTMLElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);
  const columnByID = new Map(week.columns.map((column) => [column.id, column]));
  useEffect(() => {
    const focus = () => filter.current?.focus();
    document.addEventListener("todolix:week-band", focus);
    return () => document.removeEventListener("todolix:week-band", focus);
  }, []);

  // ‹ › through the groups, as many at a time as the days show; scrolling sideways moves the count along.
  const [first, setFirst] = useState(0);
  const step = width + GAP;
  const pageTo = (index: number) => {
    columnsRef.current?.scrollTo({ left: Math.max(0, index) * step, behavior: reducesMotion() ? "auto" : "smooth" });
  };
  useEffect(() => { columnsRef.current?.scrollTo({ left: 0 }); setFirst(0); }, [week.tab]);
  const count = week.band.length;

  // The height as dragged (0: two fifths of the window, as the style sheet has it); the edge above sets it.
  const [dragged, setDragged] = useState<number | null>(null);
  const resize = useRef<{ y: number; height: number; room: number } | null>(null);
  const lastTap = useRef(0);
  const clamp = (height: number, room: number) => Math.round(Math.min(Math.max(height, BAND_MIN), Math.max(room - DAYS_MIN, BAND_MIN)));
  const room = () => band.current?.parentElement?.clientHeight ?? 0;
  const edge = {
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
      if (!week.showsBand || !band.current) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      resize.current = { y: event.clientY, height: band.current.offsetHeight, room: room() };
    },
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
      const start = resize.current;
      if (!start) return;
      setDragged(clamp(start.height - (event.clientY - start.y), start.room));
    },
    onPointerUp: (event: PointerEvent<HTMLDivElement>) => {
      const start = resize.current;
      resize.current = null;
      if (!start) return;
      const moved = Math.abs(event.clientY - start.y) > 3;
      if (moved && dragged !== null) setWeekPref({ bandHeight: dragged });
      setDragged(null);
      // A double-click, or a double tap on a touch screen, gives the height back to the window.
      if (!moved) {
        if (Date.now() - lastTap.current < 350) setWeekPref({ bandHeight: 0 });
        lastTap.current = Date.now();
      }
    },
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
      if (!week.showsBand || !band.current || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return;
      event.preventDefault();
      setWeekPref({ bandHeight: clamp(band.current.offsetHeight + (event.key === "ArrowUp" ? 24 : -24), room()) });
    },
  };
  const height = !week.showsBand ? undefined : dragged ?? (bandHeight > 0 ? clamp(bandHeight, room() || bandHeight + DAYS_MIN) : undefined);

  // A drag lands in the dragged to-do's own group (Anytime and Someday keep its project or area): only that group
  // marks it and takes a place between its rows (Bastian, 2026-10-20: every group took it).
  const landing = () => {
    const ids = draggedIDs();
    const todo = ids?.[0] !== undefined ? byID.get(ids[0]) : undefined;
    return todo ? bandSectionID(todo, week.tab, week.band) : undefined;
  };
  const takes = (sectionID: string) => () => draggedIDs() === null || landing() === sectionID;
  const markOn = (sectionID: string) => (column: HTMLElement) => {
    if (takes(sectionID)()) return column;
    const target = landing();
    return target === undefined ? null : column.parentElement?.querySelector<HTMLElement>(`[data-band-section="${CSS.escape(target)}"]`) ?? null;
  };

  return (
    <section ref={band} className={`week-band${week.showsBand ? "" : " folded"}`} aria-label={t("Unscheduled")} style={height ? { height } : undefined}>
      <div className="band-edge" role="separator" aria-orientation="horizontal" aria-label={t("Height of Unscheduled")} tabIndex={week.showsBand ? 0 : -1}
        onDoubleClick={() => setWeekPref({ bandHeight: 0 })} {...edge} />
      <div className="band-bar">
        <button className="fold band-fold" aria-expanded={week.showsBand} title={`${week.showsBand ? t("Hide Unscheduled") : t("Show Unscheduled")} (P)`}
          onClick={() => setWeekPref({ showsBand: !week.showsBand })}>
          <Icon name="chevDown" className="chev" />
          <h2>{t("Unscheduled")}</h2>
        </button>
        <div className="segmented compact band-tabs" role="tablist" aria-label={t("Unscheduled")}>
          {WEEK_BANDS.map((tab) => (
            <button key={tab} role="tab" aria-selected={week.tab === tab} className={week.tab === tab ? "on" : ""}
              onClick={() => { setWeekPref({ band: tab, showsBand: true }); set({ weekExpanded: [] }); }}
              {...headerDropProps(window, (ids) => onDrop(tab, ids, undefined))}>
              {t(BAND_TITLES[tab])} <span className="band-count">{week.counts[tab]}</span>
            </button>
          ))}
        </div>
        <span className="spacer" />
        {week.showsBand && count > visible && (
          <div className="band-pager" role="group" aria-label={t("Unscheduled")}>
            <button className="pager-button" onClick={() => pageTo(first - visible)} disabled={first === 0} aria-label={t("Previous Groups")} title={t("Previous Groups")}>
              <Icon name="chevLeft" />
            </button>
            <span className="band-position" aria-live="polite">{t("{first}–{last} of {count}", { first: first + 1, last: Math.min(first + visible, count), count })}</span>
            <button className="pager-button" onClick={() => pageTo(Math.min(first + visible, count - visible))} disabled={first + visible >= count} aria-label={t("Next Groups")} title={t("Next Groups")}>
              <Icon name="chevRight" />
            </button>
          </div>
        )}
        {week.showsBand && (
          <label className="band-filter">
            <Icon name="search" />
            <input ref={filter} className="band-filter-input" value={ui.weekFilter} placeholder={t("Filter")} aria-label={t("Filter Unscheduled")}
              onChange={(event) => set({ weekFilter: event.target.value, weekExpanded: [] })}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  if (ui.weekFilter) set({ weekFilter: "" });
                  else (event.target as HTMLInputElement).blur();
                }
                const first = week.columns.find((column) => column.id.startsWith("band:"))?.ids[0];
                if (event.key === "ArrowDown" && first) {
                  event.preventDefault();
                  set({ selected: [first] });
                  requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-row="${first}"]`)?.focus());
                }
                // ← at the start of the field goes back up into the week.
                const input = event.target as HTMLInputElement;
                if (event.key === "ArrowLeft" && input.selectionStart === 0 && input.selectionEnd === 0) {
                  const back = week.columns[0]?.ids[0];
                  event.preventDefault();
                  input.blur();
                  if (back) {
                    set({ selected: [back] });
                    requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-row="${back}"]`)?.focus());
                  }
                }
              }} />
          </label>
        )}
      </div>
      {week.showsBand && (
        week.band.length === 0 ? (
          <div className="band-empty" {...columnDropProps((ids) => onDrop(week.tab, ids, undefined))}>
            {ui.weekFilter.trim() ? t("Nothing matches.") : t("Nothing waits in {list}.", { list: t(BAND_TITLES[week.tab]) })}
          </div>
        ) : (
          <div className="band-columns" ref={columnsRef} style={{ ["--week-column" as string]: `${width}px`, ["--week-gap" as string]: `${GAP}px` }}
            onScroll={(event) => setFirst(Math.round(event.currentTarget.scrollLeft / step))}>
            {week.band.map((section) => {
              const id = `band:${section.id}`;
              const todos = (columnByID.get(id)?.ids ?? []).flatMap((todoID) => byID.get(todoID) ?? []);
              const hidden = week.hidden[id] ?? 0;
              const drop = (ids: readonly string[], before: string | undefined) => onDrop(week.tab, ids, before);
              return (
                <section key={section.id} className="week-day band-column" data-band-section={section.id} aria-label={bandGroupTitle(section, week.tab, window.state.lists)}
                  {...columnDropProps((ids) => drop(ids, undefined), { markOn: markOn(section.id) })}>
                  <BandHead section={section} tab={week.tab} />
                  {todos.map((todo, index) => (
                    <Fragment key={todo.id}>
                      {ui.open === todo.id ? <TodoCard todo={todo} /> : (
                        <TodoRow todo={todo} route={bandRoute(week.tab)} showsPlace={week.tab !== "anytime" && section.header.kind === "none"} bare={bare}
                          inToday={false} onComplete={onComplete} drop={columnRowDropProps(todo, todos[index + 1]?.id, drop, takes(section.id))} />
                      )}
                    </Fragment>
                  ))}
                  {hidden > 0 && (
                    <button className="link week-more" onClick={() => set({ weekExpanded: [...ui.weekExpanded, id] })}>
                      {t("Show {count} More", { count: hidden })}
                    </button>
                  )}
                </section>
              );
            })}
          </div>
        )
      )}
    </section>
  );
}

function bandGroupTitle(section: ListSection, tab: WeekBand, lists: Lists): string {
  const header = section.header;
  switch (header.kind) {
    case "project": return header.project.title;
    case "area": return splitEmoji(header.area.title).name;
    case "general": return t("Miscellaneous");
    case "section": return header.section.title;
    default: return routeTitle(bandRoute(tab), lists);
  }
}

/** A group's heading, as Anytime's own: the project's ring or the area's emoji, the name, › to open it. */
function BandHead({ section, tab }: { section: ListSection; tab: WeekBand }) {
  const { state, go } = useWindow();
  const header = section.header;
  if (header.kind === "project") {
    return (
      <button className="group-head" onClick={() => go({ kind: "project", id: header.project.id })}>
        <ProjectMark project={header.project} progress={state.lists.progress(header.project)} size={18} />
        <span>{header.project.title}</span><Icon name="chevRight" className="group-chev" />
      </button>
    );
  }
  if (header.kind === "area") {
    const { emoji, name } = splitEmoji(header.area.title);
    return (
      <button className="group-head" onClick={() => go({ kind: "area", id: header.area.id })}>
        {emoji && <span className="emoji">{emoji}</span>}<span>{name}</span><Icon name="chevRight" className="group-chev" />
      </button>
    );
  }
  if (header.kind === "general") return <div className="group-head static"><Icon name="misc" /><span>{t("Miscellaneous")}</span></div>;
  return <div className="group-head static"><span>{bandGroupTitle(section, tab, state.lists)}</span></div>;
}
